import { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { api, useSeo } from "../../lib/api";
import { useLang } from "../../lib/ctx";
import { CATEGORY_KEY, CONDITION_KEY } from "../../lib/i18n";
import { ProductGrid } from "../../components/store/ProductCard";
import { subUrl } from "../../components/store/Header";

const SIZES = ["S", "M", "L", "XL", "UK7", "UK8", "UK9", "UK10", "One Size"];

export default function Shop() {
  const { category } = useParams();
  const { pathname } = useLocation();
  const [sp, setSp] = useSearchParams();
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const cat = cats.find((c) => c.slug === category);
  const sub = sp.get("sub") || "";
  const tag = pathname === "/special-offers" ? "offers" : "";
  const title = category ? (cat ? (lang === "bm" ? cat.name_bm : cat.name) : t(CATEGORY_KEY[category] || "shop")) : pathname === "/new-arrivals" ? t("newArrivals") : tag ? t("offers") : t("shop");
  useSeo(title, `${title} — shop at DDVINTAGE CO, Malaysia.`, { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: "https://ddvintage.co/" }, { "@type": "ListItem", position: 2, name: title, item: `https://ddvintage.co${pathname}` }] });
  useEffect(() => {
    api.get("/catalog/meta").then((r) => { setBrands(r.data.brands); setCats(r.data.categories); });
  }, []);
  useEffect(() => {
    const p = new URLSearchParams(sp);
    if (category) p.set("category", category);
    if (tag) p.set("tag", tag);
    setItems(null);
    api.get(`/products?${p.toString()}`).then((r) => setItems(r.data));
  }, [category, tag, sp]);
  const setParam = (k, v) => {
    const n = new URLSearchParams(sp);
    v ? n.set(k, v) : n.delete(k);
    setSp(n);
  };
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-12" data-testid="shop-page">
      <p className="eyebrow">DDVINTAGE CO</p>
      <h1 className="mt-2 font-display text-5xl sm:text-6xl lg:text-7xl" data-testid="shop-title">{title}</h1>
      {sp.get("q") && <p className="mt-2 text-zinc-400">“{sp.get("q")}”</p>}
      {cat?.subs?.length > 0 && (
        <div className="no-scrollbar mt-6 flex gap-2 overflow-x-auto pb-1" data-testid="shop-sub-tabs">
          {["", ...cat.subs].map((s) => (
            <Link key={s || "all"} to={subUrl(cat.slug, s)} data-testid={`shop-sub-tab-${(s || "all").replace(/\W+/g, "-").toLowerCase()}`}
              className={`whitespace-nowrap border px-4 py-2 text-xs uppercase tracking-widest transition-colors duration-200 ${sub === s ? "border-[#C8A45D] bg-[#C8A45D] text-black" : "border-white/15 text-zinc-300 hover:border-[#C8A45D] hover:text-white"}`}>{s || t("all")}</Link>
          ))}
        </div>
      )}
      <div className="mt-8 flex flex-wrap gap-3 border-y border-white/10 py-4" data-testid="shop-filters">
        <select className="field w-auto" value={sp.get("brand") || ""} onChange={(e) => setParam("brand", e.target.value)} data-testid="filter-brand">
          <option value="">{t("allBrands")}</option>{brands.map((b) => <option key={b}>{b}</option>)}
        </select>
        <select className="field w-auto" value={sp.get("condition") || ""} onChange={(e) => setParam("condition", e.target.value)} data-testid="filter-condition">
          <option value="">{t("allConditions")}</option>{Object.entries(CONDITION_KEY).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
        </select>
        <select className="field w-auto" value={sp.get("size") || ""} onChange={(e) => setParam("size", e.target.value)} data-testid="filter-size">
          <option value="">{t("size")}</option>{SIZES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <input type="number" placeholder="Min RM" className="field w-28" value={sp.get("min_price") || ""} onChange={(e) => setParam("min_price", e.target.value)} data-testid="filter-min-price" />
        <input type="number" placeholder="Max RM" className="field w-28" value={sp.get("max_price") || ""} onChange={(e) => setParam("max_price", e.target.value)} data-testid="filter-max-price" />
        <select className="field ml-auto w-auto" value={sp.get("sort") || "newest"} onChange={(e) => setParam("sort", e.target.value)} data-testid="filter-sort">
          <option value="newest">{t("newest")}</option><option value="price_asc">{t("priceLow")}</option><option value="price_desc">{t("priceHigh")}</option><option value="bestselling">{t("bestselling")}</option>
        </select>
      </div>
      <div className="mt-10">
        {items === null ? <p className="text-zinc-500">Loading…</p> : items.length ? <ProductGrid items={items} /> : <p className="text-zinc-500" data-testid="no-products">{t("noProducts")}</p>}
      </div>
    </div>
  );
}
